import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Trophy, Medal, Crown, Flame, Users, TrendingUp, Award, ChevronRight, Calendar, History, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const AdminLeaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalUsers: 0, totalPoints: 0, activeUsers: 0, checkInDays: 0 });
  const [sendingFistBump, setSendingFistBump] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [displayInfo, setDisplayInfo] = useState({ month: '', year: '', display_name: '' });
  const [excludedCount, setExcludedCount] = useState(0);

  useEffect(() => {
    fetchAvailableMonths();
  }, []);

  useEffect(() => {
    if (selectedMonth === 'current') {
      fetchCurrentLeaderboard();
    } else {
      const [year, month] = selectedMonth.split('-').map(Number);
      fetchHistoricalLeaderboard(year, month);
    }
  }, [selectedMonth]);

  const fetchAvailableMonths = async () => {
    try {
      const res = await apiClient.get('/admin/leaderboard/history?months=24');
      setAvailableMonths(res.data);
    } catch (err) {
      console.error('Failed to fetch available months:', err);
    }
  };

  const fetchCurrentLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/leaderboard/current');
      setLeaderboard(res.data.leaderboard || []);
      setExcludedCount(res.data.excluded_count || 0);
      setDisplayInfo({
        month: res.data.month,
        year: res.data.year,
        display_name: new Date(res.data.year, res.data.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
      
      // Calculate stats from ranked users only
      const rankedUsers = res.data.leaderboard?.filter(u => u.rank !== null) || [];
      const activeUsers = rankedUsers.length;
      const totalPoints = rankedUsers.reduce((sum, u) => sum + (u.monthly_points || 0), 0);
      const totalCheckInDays = rankedUsers.reduce((sum, u) => sum + (u.check_in_days || 0), 0);
      
      // Get total users count
      const usersRes = await apiClient.get('/admin/users');
      
      setStats({
        totalUsers: usersRes.data.length,
        totalPoints,
        activeUsers,
        checkInDays: totalCheckInDays
      });
    } catch (err) {
      console.error('Failed to fetch current leaderboard:', err);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoricalLeaderboard = async (year, month) => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/leaderboard/month/${year}/${month}`);
      setLeaderboard(res.data.leaderboard || []);
      setExcludedCount(res.data.excluded_count || 0);
      setDisplayInfo({
        month: res.data.month,
        year: res.data.year,
        display_name: res.data.display_name
      });
      
      // Calculate stats from ranked users only
      const rankedUsers = res.data.leaderboard?.filter(u => u.rank !== null) || [];
      const activeUsers = rankedUsers.length;
      const totalPoints = rankedUsers.reduce((sum, u) => sum + (u.monthly_points || 0), 0);
      const totalCheckInDays = rankedUsers.reduce((sum, u) => sum + (u.check_in_days || 0), 0);
      
      setStats({
        totalUsers: activeUsers,
        totalPoints,
        activeUsers,
        checkInDays: totalCheckInDays
      });
    } catch (err) {
      console.error('Failed to fetch historical leaderboard:', err);
      toast.error('Failed to load historical leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const sendFistBump = async (toUserId, userName) => {
    setSendingFistBump(toUserId);
    const today = new Date().toISOString().split('T')[0];
    try {
      await apiClient.post('/reactions', {
        to_user_id: toUserId,
        date: today,
        reaction_type: 'fistbump'
      });
      toast.success(`👊 Fist bump sent to ${userName}!`);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send fist bump';
      toast.error(message);
    } finally {
      setSendingFistBump(null);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="text-yellow-400" size={20} />;
    if (rank === 2) return <Medal className="text-gray-400" size={20} />;
    if (rank === 3) return <Medal className="text-amber-600" size={20} />;
    return <span className="text-stone-500 font-bold text-sm">#{rank}</span>;
  };

  const getRankStyle = (rank, isExcluded) => {
    if (isExcluded) return 'bg-stone-100/20 border-l-4 border-stone-300 opacity-60';
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-transparent border-l-4 border-yellow-500';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-transparent border-l-4 border-gray-400';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-transparent border-l-4 border-amber-600';
    return 'border-l-4 border-transparent';
  };

  const isHistorical = selectedMonth !== 'current';
  
  // Get top 3 ranked users for podium (exclude users with null rank)
  const rankedLeaderboard = leaderboard.filter(entry => entry.rank !== null);
  const excludedUsers = leaderboard.filter(entry => entry.rank === null);
  const top3 = rankedLeaderboard.slice(0, 3);

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        {/* Header with Month Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 flex items-center gap-3">
              <Trophy className="text-[#EFB342]" size={32} />
              LEADERBOARD
            </h1>
            <p className="text-stone-600 text-sm mt-1">
              {isHistorical ? (
                <span className="flex items-center gap-1">
                  <History size={14} />
                  Historical View: {displayInfo.display_name}
                </span>
              ) : (
                `${displayInfo.display_name} Challenge Rankings`
              )}
            </p>
          </div>
          
          {/* Month Selector */}
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-stone-500" />
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[200px] bg-white border-stone-300 text-stone-900">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent className="bg-white border-stone-300">
                <SelectItem value="current" className="text-stone-900 hover:bg-stone-100">
                  Current Month
                </SelectItem>
                {availableMonths.map((m) => (
                  <SelectItem 
                    key={m.year_month} 
                    value={m.year_month}
                    className="text-stone-900 hover:bg-stone-100"
                  >
                    {m.display_name} ({m.total_points.toLocaleString()} pts)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#EFB342]"></div>
          </div>
        ) : (
          <>
            {/* Top 3 Podium - shows with 1, 2, or 3 users */}
            {top3.length >= 1 && (
              <div className="flex items-end justify-center gap-2 md:gap-4 mb-8">
                {/* 2nd Place */}
                {top3.length >= 2 && (
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-gray-400/20 border-2 border-gray-400 flex items-center justify-center mb-2 md:mb-3">
                      <Medal className="text-gray-400" size={24} />
                    </div>
                    <Link 
                      to={`/admin/users/${top3[1].user_id}`}
                      className="text-stone-900 font-semibold truncate max-w-[80px] md:max-w-[120px] text-sm md:text-base hover:text-[#EFB342] transition-colors text-center"
                    >
                      {top3[1].user_name}
                    </Link>
                    <p className="text-stone-500 text-sm">{top3[1].monthly_points} pts</p>
                    <div className="w-16 h-16 md:w-24 md:h-24 bg-gray-400/20 rounded-t-xl mt-2 flex items-center justify-center">
                      <span className="text-2xl md:text-3xl font-bold text-gray-400">2</span>
                    </div>
                  </div>
                )}
                
                {/* 1st Place - always shown if there's at least 1 user */}
                <div className="flex flex-col items-center">
                  <div className="w-[72px] h-[72px] md:w-24 md:h-24 rounded-full bg-yellow-500/20 border-2 border-yellow-400 flex items-center justify-center mb-2 md:mb-3 shadow-lg shadow-yellow-500/20">
                    <Crown className="text-yellow-400" size={32} />
                  </div>
                  <Link 
                    to={`/admin/users/${top3[0].user_id}`}
                    className="text-stone-900 font-semibold truncate max-w-[100px] md:max-w-[140px] text-sm md:text-lg hover:text-[#EFB342] transition-colors text-center"
                  >
                    {top3[0].user_name}
                  </Link>
                  <p className="text-stone-500 text-sm md:text-base">{top3[0].monthly_points} pts</p>
                  <div className="w-20 h-24 md:w-28 md:h-32 bg-yellow-500/20 rounded-t-xl mt-2 flex items-center justify-center">
                    <span className="text-3xl md:text-4xl font-bold text-yellow-400">1</span>
                  </div>
                </div>
                
                {/* 3rd Place */}
                {top3.length >= 3 && (
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-amber-600/20 border-2 border-amber-600 flex items-center justify-center mb-2 md:mb-3">
                      <Medal className="text-amber-600" size={24} />
                    </div>
                    <Link 
                      to={`/admin/users/${top3[2].user_id}`}
                      className="text-stone-900 font-semibold truncate max-w-[80px] md:max-w-[120px] text-sm md:text-base hover:text-[#EFB342] transition-colors text-center"
                    >
                      {top3[2].user_name}
                    </Link>
                    <p className="text-stone-500 text-sm">{top3[2].monthly_points} pts</p>
                    <div className="w-16 h-14 md:w-24 md:h-20 bg-amber-600/20 rounded-t-xl mt-2 flex items-center justify-center">
                      <span className="text-2xl md:text-3xl font-bold text-amber-600">3</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                <Users className="text-[#EFB342] mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-stone-900">{stats.activeUsers}</p>
                <p className="text-xs text-stone-500">{isHistorical ? 'Participants' : 'Active Users'}</p>
              </div>
              <div className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                <Award className="text-purple-500 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-stone-900">{stats.totalPoints.toLocaleString()}</p>
                <p className="text-xs text-stone-500">Total Points</p>
              </div>
              <div className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                <TrendingUp className="text-green-500 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-stone-900">{stats.checkInDays}</p>
                <p className="text-xs text-stone-500">Check-in Days</p>
              </div>
              <div className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                <Flame className="text-orange-500 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-stone-900">
                  {stats.activeUsers > 0 ? Math.round(stats.totalPoints / stats.activeUsers) : 0}
                </p>
                <p className="text-xs text-stone-500">Avg Points/User</p>
              </div>
            </div>

            {/* Historical Banner */}
            {isHistorical && (
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-center gap-3">
                <History className="text-blue-400" size={20} />
                <div>
                  <p className="text-blue-400 font-medium text-sm">Viewing Historical Data</p>
                  <p className="text-stone-500 text-xs">This shows the final results for {displayInfo.display_name}</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setSelectedMonth('current')}
                  className="ml-auto border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                >
                  Back to Current
                </Button>
              </div>
            )}

            {/* Leaderboard List */}
            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-stone-200">
                <h2 className="font-heading text-lg font-bold text-stone-900">
                  {isHistorical ? 'ALL PARTICIPANTS' : 'MONTHLY RANKINGS'}
                </h2>
                <p className="text-sm text-stone-500">
                  Points earned from 1st - 28th of {displayInfo.display_name}
                </p>
              </div>

              {rankedLeaderboard.length === 0 && excludedUsers.length === 0 ? (
                <div className="p-8 text-center text-stone-500">
                  <Trophy className="mx-auto mb-3 text-stone-400" size={48} />
                  <p>No rankings for this month</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {/* Ranked Users */}
                  {rankedLeaderboard.map((entry) => (
                    <div
                      key={entry.user_id}
                      className={`flex items-center justify-between p-4 hover:bg-[#F0EBE1] transition-colors ${getRankStyle(entry.rank, false)}`}
                      data-testid={`leaderboard-entry-${entry.user_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 flex justify-center">
                          {getRankIcon(entry.rank)}
                        </div>
                        <div>
                          <Link 
                            to={`/admin/users/${entry.user_id}`}
                            className="font-semibold text-stone-900 hover:text-[#EFB342] transition-colors flex items-center gap-2"
                          >
                            {entry.user_name}
                            <ChevronRight size={16} className="text-stone-400" />
                          </Link>
                          <div className="flex items-center gap-3 text-sm text-stone-500">
                            <span className="text-xs">{entry.email}</span>
                            <span>{entry.check_in_days || 0} days</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xl font-bold text-[#EFB342]">{entry.monthly_points || 0}</p>
                          <p className="text-xs text-stone-500">points</p>
                        </div>
                        {!isHistorical && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => sendFistBump(entry.user_id, entry.user_name)}
                            disabled={sendingFistBump === entry.user_id}
                            className="text-2xl hover:bg-stone-200/50 px-3"
                            title={`Send fist bump to ${entry.user_name}`}
                          >
                            {sendingFistBump === entry.user_id ? (
                              <span className="animate-pulse">👊</span>
                            ) : (
                              <span>👊</span>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Excluded Users Section */}
                  {excludedUsers.length > 0 && (
                    <>
                      <div className="p-3 bg-stone-50 flex items-center gap-2">
                        <EyeOff size={16} className="text-stone-500" />
                        <span className="text-stone-600 text-sm font-medium">
                          Hidden from Public Leaderboard ({excludedUsers.length})
                        </span>
                      </div>
                      {excludedUsers.map((entry) => (
                        <div
                          key={entry.user_id}
                          className={`flex items-center justify-between p-4 hover:bg-[#F0EBE1] transition-colors ${getRankStyle(null, true)}`}
                          data-testid={`leaderboard-entry-excluded-${entry.user_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 flex justify-center">
                              <EyeOff size={16} className="text-stone-400" />
                            </div>
                            <div>
                              <Link 
                                to={`/admin/users/${entry.user_id}`}
                                className="font-semibold text-stone-600 hover:text-[#EFB342] transition-colors flex items-center gap-2"
                              >
                                {entry.user_name}
                                <span className="text-xs text-amber-500 bg-amber-500/20 px-2 py-0.5 rounded">Hidden</span>
                                <ChevronRight size={16} className="text-stone-400" />
                              </Link>
                              <div className="flex items-center gap-3 text-sm text-stone-400">
                                <span className="text-xs">{entry.email}</span>
                                <span>{entry.check_in_days || 0} days</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-xl font-bold text-stone-500">{entry.monthly_points || 0}</p>
                              <p className="text-xs text-stone-400">points</p>
                            </div>
                            {!isHistorical && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => sendFistBump(entry.user_id, entry.user_name)}
                                disabled={sendingFistBump === entry.user_id}
                                className="text-2xl hover:bg-stone-200/50 px-3 opacity-60"
                                title={`Send fist bump to ${entry.user_name}`}
                              >
                                {sendingFistBump === entry.user_id ? (
                                  <span className="animate-pulse">👊</span>
                                ) : (
                                  <span>👊</span>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Info Note */}
            <div className="mt-4 p-4 bg-[#F0EBE1] border border-stone-200 rounded-xl">
              <p className="text-sm text-stone-500">
                <strong className="text-stone-600">💡 Tip:</strong> Use the month selector to view past challenge results. 
                Users marked as "Hidden" are excluded from the public leaderboard but their stats are still tracked.
              </p>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminLeaderboard;
